import { localDb } from './localDbService';
import { HistoryEntry, WaterLog, UserGoals, ScannedProduct } from '../types';
import { safeClone, ensureTimestampMillis } from '../lib/utils';
import { toast } from 'sonner';

export interface RestoredUserDataPayload {
  uid: string;
  email: string;
  userName: string;
  goals: UserGoals;
  metrics: {
    height: number;
    weight: number;
    age: number;
    gender: string;
    activity: number;
    goal: string;
  };
  metricsSummary: string;
  foodPreferences?: string;
  safeChoiceProfile?: any;
  favorites: HistoryEntry[];
  skin?: string;
  illuminationColor?: string;
  timeFormat?: string;
  soundEnabled?: boolean;
  scannedProducts: ScannedProduct[];
  groceryList?: any[];
  history: HistoryEntry[];
  waterLogs: WaterLog[];
  days?: Record<string, any>;
}

export interface RestorationResult {
  restored: boolean;
  data?: RestoredUserDataPayload;
  source?: string;
}

const TARGET_EMAIL = 'kim41k@gmail.com';
const TARGET_UID = 'tDksKsL8M2cqQHQRPU19NnQATRU2';

/**
 * Checks whether the current user is eligible for automatic historical archive restoration.
 */
export function isEligibleForRestoration(currentUser?: any): boolean {
  const email = (
    currentUser?.email ||
    currentUser?.providerData?.find((p: any) => p?.email)?.email ||
    (typeof localStorage !== 'undefined' ? localStorage.getItem('user_email') : '') ||
    ''
  ).toLowerCase().trim();

  const uid = currentUser?.uid || (typeof localStorage !== 'undefined' ? localStorage.getItem('user_uid') : '') || '';
  const userName = (typeof localStorage !== 'undefined' ? localStorage.getItem('userName') : '') || '';

  return email === TARGET_EMAIL || uid === TARGET_UID || userName.trim().toLowerCase() === 'mr.gray';
}

/**
 * Fetch restored user data archive from bundled JSON chunk, CDN/static asset, or fallback API endpoint.
 */
export async function fetchRestoredUserDataArchive(): Promise<RestoredUserDataPayload | null> {
  // 1. Static asset / API endpoint fetch (preferred, fast, streamable)
  const sources = [
    '/restoredUserData.json',
    '/api/user/restored-data',
    './restoredUserData.json',
    '/restoredUserDataCompact.json',
    './restoredUserDataCompact.json'
  ];

  for (const src of sources) {
    try {
      const res = await fetch(src, { cache: 'no-store' });
      if (res.ok) {
        const json = await res.json();
        const payload = (json?.data && Array.isArray(json.data.history)) ? json.data : json;
        if (payload && Array.isArray(payload.history) && payload.history.length > 0) {
          console.log(`[DATA-RESTORE] Loaded archive from ${src} with ${payload.history.length} meals and ${payload.waterLogs?.length || 0} water logs.`);
          return payload as RestoredUserDataPayload;
        }
      }
    } catch (e) {
      console.warn(`[DATA-RESTORE] Source ${src} failed to load:`, e);
    }
  }

  // 2. Bundled dynamic import fallback (100% offline resilient)
  try {
    const mod = await import('../data/restoredUserDataCompact.json');
    const data = (mod.default || mod) as any;
    if (data && Array.isArray(data.history) && data.history.length > 0) {
      console.log(`[DATA-RESTORE] Loaded bundled archive with ${data.history.length} meals and ${data.waterLogs?.length || 0} water logs.`);
      return data as RestoredUserDataPayload;
    }
  } catch (err) {
    console.warn('[DATA-RESTORE] Dynamic import fallback failed:', err);
  }

  return null;
}

/**
 * Persists the restored archive into localDb, localStorage, and returns the merged dataset.
 */
export function applyRestoredUserData(
  archive: RestoredUserDataPayload,
  existingHistory: HistoryEntry[] = [],
  existingWaterLogs: WaterLog[] = []
): {
  mergedHistory: HistoryEntry[];
  mergedWaterLogs: WaterLog[];
  userName: string;
  goals: UserGoals;
  metrics: any;
  favorites: HistoryEntry[];
  scannedProducts: ScannedProduct[];
} {
  const userUid = archive.uid || TARGET_UID;

  // 1. Merge History: Deduplicate by item.id, preserving any newly logged entries AND meal photos
  const mealMap = new Map<string, HistoryEntry>();
  archive.history.forEach(m => {
    if (m && m.id) {
      mealMap.set(m.id, {
        ...m,
        timestamp: ensureTimestampMillis(m.timestamp)
      });
    }
  });
  existingHistory.forEach(m => {
    if (m && m.id) {
      const archItem = mealMap.get(m.id);
      // Prioritize existing non-empty image, but if existing is empty/missing and archive has it, restore it!
      const resolvedImage = (m.image && m.image.trim() !== '')
        ? m.image
        : (archItem?.image || '');

      mealMap.set(m.id, {
        ...(archItem || {}),
        ...m,
        image: resolvedImage,
        timestamp: ensureTimestampMillis(m.timestamp)
      });
    }
  });
  const mergedHistory = Array.from(mealMap.values()).sort((a, b) => b.timestamp - a.timestamp);

  // 2. Merge Water Logs: Deduplicate by item.id
  const waterMap = new Map<string, WaterLog>();
  archive.waterLogs.forEach(w => {
    if (w && w.id) {
      waterMap.set(w.id, {
        ...w,
        timestamp: ensureTimestampMillis(w.timestamp)
      });
    }
  });
  existingWaterLogs.forEach(w => {
    if (w && w.id) {
      waterMap.set(w.id, {
        ...w,
        timestamp: ensureTimestampMillis(w.timestamp)
      });
    }
  });
  const mergedWaterLogs = Array.from(waterMap.values()).sort((a, b) => b.timestamp - a.timestamp);

  // 3. Save into localDb (IndexedDB unlimited store)
  try {
    localDb.setItem('meal_history', JSON.stringify(safeClone(mergedHistory)));
    localDb.setItem('water_logs', JSON.stringify(safeClone(mergedWaterLogs)));
    if (archive.goals) {
      localDb.setItem('user_goals', JSON.stringify(safeClone(archive.goals)));
    }
    if (archive.userName) {
      localDb.setItem('userName', archive.userName);
    }
    if (archive.favorites && archive.favorites.length > 0) {
      localDb.setItem('favorites', JSON.stringify(safeClone(archive.favorites)));
    }
    if (archive.scannedProducts && archive.scannedProducts.length > 0) {
      localDb.setItem('scanned_products', JSON.stringify(safeClone(archive.scannedProducts)));
    }
  } catch (err) {
    console.error('[DATA-RESTORE] Error writing to localDb:', err);
  }

  // 4. Save into localStorage
  try {
    if (archive.userName) {
      localStorage.setItem('userName', archive.userName);
    }
    if (archive.goals) {
      localStorage.setItem('user_goals', JSON.stringify(safeClone(archive.goals)));
    }
    if (archive.metrics) {
      localStorage.setItem('onboardingGender', archive.metrics.gender || 'male');
      localStorage.setItem('onboardingAge', String(archive.metrics.age || 38));
      localStorage.setItem('onboardingWeight', String(archive.metrics.weight || 70));
      localStorage.setItem('onboardingHeight', String(archive.metrics.height || 184));
      localStorage.setItem('onboardingActivity', String(archive.metrics.activity || 1.2));
      localStorage.setItem('onboardingGoal', archive.metrics.goal || 'maintain');
    }
    if (archive.metricsSummary) {
      localStorage.setItem('user_metrics_summary', archive.metricsSummary);
    }
    if (archive.foodPreferences) {
      localStorage.setItem('food_preferences', archive.foodPreferences);
    }
    // Ensure favorites have images restored from mealMap if present
    const enrichedFavorites = (archive.favorites || []).map(fav => {
      if (fav && fav.id && (!fav.image || fav.image.trim() === '')) {
        const matching = mealMap.get(fav.id);
        if (matching && matching.image) {
          return { ...fav, image: matching.image };
        }
      }
      return fav;
    });

    if (enrichedFavorites.length > 0) {
      localDb.setItem('favorites', JSON.stringify(safeClone(enrichedFavorites)));
      localStorage.setItem('favorites', JSON.stringify(safeClone(enrichedFavorites)));
    }
    if (archive.scannedProducts) {
      localStorage.setItem('scanned_products', JSON.stringify(safeClone(archive.scannedProducts)));
    }
    localStorage.setItem('onboarding_completed', 'true');
    localStorage.setItem(`user_history_restored_v1_${userUid}`, 'true');
    localStorage.setItem(`user_history_restored_v2_${userUid}`, 'true');
  } catch (err) {
    console.error('[DATA-RESTORE] Error writing to localStorage:', err);
  }

  console.log(`[DATA-RESTORE] Successfully applied restoration! ${mergedHistory.length} meals, ${mergedWaterLogs.length} water logs.`);

  return {
    mergedHistory,
    mergedWaterLogs,
    userName: archive.userName || 'Mr.Gray',
    goals: archive.goals,
    metrics: archive.metrics,
    favorites: archive.favorites || [],
    scannedProducts: archive.scannedProducts || []
  };
}

/**
 * Checks and restores user data if eligible and needed.
 */
export async function restoreUserDataIfEligible(
  currentUser?: any,
  currentHistoryCount: number = 0,
  existingHistory: HistoryEntry[] = [],
  existingWaterLogs: WaterLog[] = []
): Promise<RestorationResult> {
  if (!isEligibleForRestoration(currentUser)) {
    return { restored: false };
  }

  const userUid = currentUser?.uid || (typeof localStorage !== 'undefined' ? localStorage.getItem('user_uid') : '') || TARGET_UID;
  const isRestoredV2 = typeof localStorage !== 'undefined' && localStorage.getItem(`user_history_restored_v2_${userUid}`) === 'true';

  // Count existing meals with photos
  const existingImagesCount = existingHistory.filter(h => h && h.image && h.image.trim().length > 0).length;
  // If fewer than 100 meals have photos, photos need to be restored (archive has 251 photos)
  const needsImageRestoration = existingImagesCount < 100;

  // Auto-restore if:
  // 1. Not yet restored to V2
  // 2. OR history count is depleted (< 200)
  // 3. OR meals are missing their photos (< 100 with images)
  if (isRestoredV2 && currentHistoryCount >= 200 && !needsImageRestoration) {
    return { restored: false };
  }

  console.log(`[DATA-RESTORE] Initiating data restoration for ${userUid} (current count: ${currentHistoryCount}, images count: ${existingImagesCount}, needsImageRestoration: ${needsImageRestoration})...`);

  const archive = await fetchRestoredUserDataArchive();
  if (!archive) {
    console.warn('[DATA-RESTORE] Failed to fetch restored archive.');
    return { restored: false };
  }

  applyRestoredUserData(archive, existingHistory, existingWaterLogs);

  return {
    restored: true,
    data: archive,
    source: 'archive'
  };
}

/**
 * Force manual restoration of historical archive by user action.
 */
export async function forceRestoreUserData(
  currentUser?: any,
  existingHistory: HistoryEntry[] = [],
  existingWaterLogs: WaterLog[] = []
): Promise<RestorationResult> {
  const archive = await fetchRestoredUserDataArchive();
  if (!archive) {
    toast.error('Не вдалося завантажити архів відновлення даних');
    return { restored: false };
  }

  const result = applyRestoredUserData(archive, existingHistory, existingWaterLogs);
  toast.success(`Історію успішно відновлено! (${result.mergedHistory.length} прийомів їжі, ${result.mergedWaterLogs.length} записів води)`, {
    duration: 6000
  });

  return {
    restored: true,
    data: archive,
    source: 'manual'
  };
}
